// Thin wrapper over amazon-cognito-identity-js. Active only when the pool +
// client id are configured at build time (production); otherwise the app uses
// the demo auth fallback in auth.jsx so local dev keeps working.
import {
  CognitoUserPool,
  CognitoUser,
  CognitoIdToken,
  AuthenticationDetails,
  CognitoUserAttribute,
} from 'amazon-cognito-identity-js'

// Injected by webpack DefinePlugin at build time (deploy.py sets the env vars
// from terraform outputs). Falls back to {} in plain local dev.
const CONFIG = globalThis.__COGNITO_CONFIG__ || {}
const USER_POOL_ID = CONFIG.userPoolId
const CLIENT_ID = CONFIG.clientId

export const isCognitoConfigured = Boolean(USER_POOL_ID && CLIENT_ID)

function pool() {
  return new CognitoUserPool({ UserPoolId: USER_POOL_ID, ClientId: CLIENT_ID })
}

// The pool marks given_name / family_name required with min_length 1, so a name
// part the user leaves blank can't be stored empty — it goes in as this sentinel
// and reads back as "unset" (matching the signup path and the invite Lambda).
const BLANK = '-'

// Cognito caps both attributes at 50 characters (modules/cognito/main.tf).
export const NAME_MAX = 50

function claimName(value) {
  const trimmed = String(value || '').trim()
  return trimmed === BLANK ? '' : trimmed
}

// Last-resort name for someone who has never set one: the email local-part,
// tidied so it reads as a name rather than a login ("jane.doe" -> "Jane Doe").
// Doing it here — instead of with a `capitalize` class at one call site — is
// what keeps a REAL name rendering exactly as the user typed it.
function fromEmail(email) {
  return String(email || '')
    .split('@')[0]
    .replace(/[._]+/g, ' ')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ')
}

// The one place a display name is derived: first + last when set, else the email
// local-part, so a user who never set a name still gets a non-blank header.
export function displayName({ email = '', firstName = '', lastName = '' } = {}) {
  return [firstName, lastName].filter(Boolean).join(' ') || fromEmail(email) || 'User'
}

function profileFromClaims(email, payload = {}) {
  const firstName = claimName(payload.given_name)
  const lastName = claimName(payload.family_name)
  return {
    email,
    firstName,
    lastName,
    name: displayName({ email, firstName, lastName }),
    role: payload['custom:role'] || 'Member',
  }
}

function profileFromSession(email, session) {
  const idToken = session.getIdToken()
  return {
    ...profileFromClaims(email, idToken.decodePayload() || {}),
    idToken: idToken.getJwtToken(),
  }
}

// Name claims out of an already-issued ID token, with no network call — used to
// backfill sessions that were persisted before first/last were tracked apart.
export function nameFromIdToken(jwt) {
  try {
    const payload = new CognitoIdToken({ IdToken: jwt }).decodePayload() || {}
    return { firstName: claimName(payload.given_name), lastName: claimName(payload.family_name) }
  } catch {
    return { firstName: '', lastName: '' }
  }
}

// Resolves { status: 'OK', user } or { status: 'NEW_PASSWORD_REQUIRED',
// setPassword(newPassword) } (invited users must set a password on first login).
export function signIn(email, password) {
  const username = email.trim().toLowerCase()
  const user = new CognitoUser({ Username: username, Pool: pool() })
  const details = new AuthenticationDetails({ Username: username, Password: password })

  return new Promise((resolve, reject) => {
    user.authenticateUser(details, {
      onSuccess: (session) => resolve({ status: 'OK', user: profileFromSession(username, session) }),
      onFailure: (err) => reject(new Error(err.message || 'Sign in failed')),
      newPasswordRequired: () => {
        resolve({
          status: 'NEW_PASSWORD_REQUIRED',
          setPassword: (newPassword) =>
            new Promise((res, rej) => {
              user.completeNewPasswordChallenge(
                newPassword,
                {},
                {
                  onSuccess: (session) =>
                    res(profileFromSession(username, session)),
                  onFailure: (err) => rej(new Error(err.message || 'Could not set password')),
                },
              )
            }),
        })
      },
    })
  })
}

export function signUp({ email, password, name }) {
  const username = email.trim().toLowerCase()
  const [given, ...rest] = name.trim().split(/\s+/)
  const attributes = [
    new CognitoUserAttribute({ Name: 'email', Value: username }),
    new CognitoUserAttribute({ Name: 'given_name', Value: given || username }),
    new CognitoUserAttribute({ Name: 'family_name', Value: rest.join(' ') || BLANK }),
  ]
  return new Promise((resolve, reject) => {
    pool().signUp(username, password, attributes, null, (err) => {
      if (err) reject(new Error(err.message || 'Sign up failed'))
      else resolve({ email: username })
    })
  })
}

export function confirmSignUp(email, code) {
  const user = new CognitoUser({ Username: email.trim().toLowerCase(), Pool: pool() })
  return new Promise((resolve, reject) => {
    user.confirmRegistration(code.trim(), true, (err) => {
      if (err) reject(new Error(err.message || 'Confirmation failed'))
      else resolve(true)
    })
  })
}

// Cognito's own wording when an app client isn't allowed to write an attribute.
// Surfaced verbatim-ish rather than swallowed: the caller must never quietly
// keep a name that only exists in this browser.
function describeUpdateError(err) {
  const code = err?.code || err?.name || ''
  const message = err?.message || 'Could not save your name.'
  if (/unauthorized attribute|not authorized to (update|write)/i.test(message)) {
    return (
      'Cognito refused the write: this app client is not allowed to update ' +
      'given_name / family_name. Your name was NOT saved — an admin has to grant ' +
      "the attributes under App client → Attribute read and write permissions. " +
      `(${message})`
    )
  }
  if (code === 'NotAuthorizedException')
    return 'Your session has expired. Sign in again, then save your name.'
  if (code === 'InvalidParameterException' || code === 'InvalidLambdaResponseException')
    return `Cognito rejected the name: ${message}`
  return message
}

// Writes given_name / family_name for the *signed-in* user with their own access
// token (no admin API — the app client has no secret, see modules/cognito).
// Resolves the refreshed profile so the name follows the account to any device.
export function updateName(email, { firstName, lastName } = {}) {
  const first = String(firstName || '').trim()
  const last = String(lastName || '').trim()
  if (!isCognitoConfigured)
    return Promise.reject(new Error('Cognito is not configured for this build.'))
  if (first.length > NAME_MAX || last.length > NAME_MAX)
    return Promise.reject(new Error(`Each name has to be ${NAME_MAX} characters or fewer.`))

  return new Promise((resolve, reject) => {
    const current = pool().getCurrentUser()
    if (!current)
      return reject(new Error('Your session has expired. Sign in again, then save your name.'))
    current.getSession((sessionErr, session) => {
      if (sessionErr || !session?.isValid())
        return reject(new Error('Your session has expired. Sign in again, then save your name.'))
      const attributes = [
        new CognitoUserAttribute({ Name: 'given_name', Value: first || BLANK }),
        new CognitoUserAttribute({ Name: 'family_name', Value: last || BLANK }),
      ]
      current.updateAttributes(attributes, (err) => {
        if (err) return reject(new Error(describeUpdateError(err)))
        // updateAttributes does not mint new tokens: without this refresh the
        // cached ID token keeps the old claims and a reload would show the old
        // name. If the refresh itself fails the write still succeeded, so report
        // the new name — the next sign-in re-reads it from the pool anyway.
        // No `role` here: it isn't part of this write, so the caller's stays put.
        const fallback = {
          email,
          firstName: first,
          lastName: last,
          name: displayName({ email, firstName: first, lastName: last }),
          idToken: session.getIdToken().getJwtToken(),
        }
        try {
          current.refreshSession(session.getRefreshToken(), (refreshErr, fresh) => {
            if (refreshErr || !fresh) return resolve(fallback)
            resolve(profileFromSession(email, fresh))
          })
        } catch {
          resolve(fallback)
        }
      })
    })
  })
}

export function signOut() {
  const current = pool().getCurrentUser()
  if (current) current.signOut()
}

// Kick off a password reset — Cognito emails a verification code to the user.
export function forgotPassword(email) {
  const user = new CognitoUser({ Username: email.trim().toLowerCase(), Pool: pool() })
  return new Promise((resolve, reject) => {
    user.forgotPassword({
      onSuccess: () => resolve(true),
      onFailure: (err) => reject(new Error(err.message || 'Could not start password reset')),
    })
  })
}

// Complete the reset with the emailed code + a new password.
export function confirmForgotPassword(email, code, newPassword) {
  const user = new CognitoUser({ Username: email.trim().toLowerCase(), Pool: pool() })
  return new Promise((resolve, reject) => {
    user.confirmPassword(code.trim(), newPassword, {
      onSuccess: () => resolve(true),
      onFailure: (err) => reject(new Error(err.message || 'Could not reset password')),
    })
  })
}

// A currently-valid ID token for authorizing API calls (e.g. the Gmail
// integration). Resolves a fresh token via the Cognito session (auto-refreshing
// with the refresh token when the cached one has expired), or '' when Cognito
// isn't configured / there's no session — callers treat '' as "not signed in".
export function getIdToken() {
  if (!isCognitoConfigured) return Promise.resolve('')
  return new Promise((resolve) => {
    const current = pool().getCurrentUser()
    if (!current) return resolve('')
    current.getSession((err, session) => {
      if (!err && session?.isValid()) return resolve(session.getIdToken().getJwtToken())
      // getSession does not always refresh for us — when it hands back an
      // expired session (ID tokens last an hour) spend the refresh token
      // explicitly before declaring the session dead. Without this the app
      // kept showing the user as signed in while every authed call went out
      // with no Authorization header and the server answered "Missing bearer
      // token".
      const refresh = session?.getRefreshToken?.()
      if (!refresh?.getToken?.()) return resolve('')
      current.refreshSession(refresh, (refreshErr, fresh) => {
        resolve(refreshErr || !fresh?.isValid() ? '' : fresh.getIdToken().getJwtToken())
      })
    })
  })
}

// The signed-in user's ACCESS token, refreshed the same way as getIdToken, or
// ''. Only for calls that act on the user's own Cognito account (changing your
// own password): the ID token proves who you are to our lambdas, the access
// token is what Cognito's own self-service APIs take.
export function getAccessToken() {
  if (!isCognitoConfigured) return Promise.resolve('')
  return new Promise((resolve) => {
    const current = pool().getCurrentUser()
    if (!current) return resolve('')
    current.getSession((err, session) => {
      if (!err && session?.isValid()) return resolve(session.getAccessToken().getJwtToken())
      const refresh = session?.getRefreshToken?.()
      if (!refresh?.getToken?.()) return resolve('')
      current.refreshSession(refresh, (refreshErr, fresh) => {
        resolve(refreshErr || !fresh?.isValid() ? '' : fresh.getAccessToken().getJwtToken())
      })
    })
  })
}

// The token, or a thrown error that says what to do about it. Use this on any
// call that REQUIRES auth: failing here with a human message beats sending a
// headerless request and surfacing the server's "Missing bearer token".
export async function requireIdToken() {
  const token = await getIdToken()
  if (token) return token
  if (!isCognitoConfigured) return ''
  // Tell the app so it can prompt for a fresh sign-in once, centrally.
  try {
    window.dispatchEvent(new CustomEvent('auth:expired'))
  } catch {
    /* non-browser context */
  }
  const err = new Error('Your session expired — sign in again to continue.')
  err.code = 'SESSION_EXPIRED'
  throw err
}
